// Store CRO Audit API client.
//
// Mirrors `tools/image-studio/imageStudioApi.js` shape — `apiFetch` wrapper
// for every request so the Bearer token is attached, JSON-parse errors
// rendered as readable strings, presigned-URL pattern for downloads.
//
// The Lambda's `store-cro-audit-api` API Gateway URL gets baked in here
// once `deploy_api.sh` has run and the API Gateway exists. Until then the
// VITE_STORE_CRO_AUDIT_API env var lets dev mode point at a local copy.

import { apiFetch } from '../../lib/apiFetch.js';
import { API_HOSTS } from '../../config.js';

const API_BASE_URL = API_HOSTS.storeCROAudit;

/** Try to parse a non-OK fetch response into a usable error message. */
async function readErrorMessage(response, fallback) {
  try {
    const cloned = response.clone();
    const data = await cloned.json();
    if (data && data.error) return data.error;
  } catch { /* fall through to text */ }
  try {
    const text = await response.text();
    if (text) return text.slice(0, 300);
  } catch { /* ignore */ }
  return fallback || `HTTP ${response.status}`;
}

export const storeCROAuditApi = {
  // ── Registry (the main view) ────────────────────────────────────────
  async getRegistry() {
    const response = await apiFetch(`${API_BASE_URL}/api/registry`);
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Failed to load registry: ${response.status}`));
    }
    return response.json();
  },

  // ── On-demand audits ─────────────────────────────────────────────────
  async submitSingleStore({ shop_id, store_url, settings = {} }) {
    const response = await apiFetch(`${API_BASE_URL}/api/audit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mode: 'single', shop_id, store_url, settings }),
    });
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Audit submit failed: ${response.status}`));
    }
    return response.json();
  },

  async submitPasted({ stores, settings = {} }) {
    const response = await apiFetch(`${API_BASE_URL}/api/audit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mode: 'paste', stores, settings }),
    });
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Audit submit failed: ${response.status}`));
    }
    return response.json();
  },

  async submitCsv(file, settings = {}) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('pdp_sample_count', String(settings.pdp_sample_count ?? 0));
    if (settings.pagespeed_keys) formData.append('pagespeed_keys', settings.pagespeed_keys);
    const response = await apiFetch(`${API_BASE_URL}/api/audit`, {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Audit submit failed: ${response.status}`));
    }
    return response.json();
  },

  // ── Status polling ───────────────────────────────────────────────────
  async getStatus(glueRunId) {
    const response = await apiFetch(`${API_BASE_URL}/api/status/${encodeURIComponent(glueRunId)}`);
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Status check failed: ${response.status}`));
    }
    return response.json();
  },

  async getLogs(glueRunId) {
    const response = await apiFetch(`${API_BASE_URL}/api/logs/${encodeURIComponent(glueRunId)}`);
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Logs fetch failed: ${response.status}`));
    }
    return response.json();
  },

  // ── Per-store history + per-run details ─────────────────────────────
  async getStoreHistory(shopId) {
    const response = await apiFetch(`${API_BASE_URL}/api/stores/${shopId}/history`);
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `History fetch failed: ${response.status}`));
    }
    return response.json();
  },

  async getRunDetails(shopId, runFilename) {
    // run_filename comes from the history entry's `key`. Strip leading path
    // components so the URL is clean — the backend ignores any path beyond
    // the filename anyway.
    const tail = runFilename.split('/').pop();
    const response = await apiFetch(
      `${API_BASE_URL}/api/stores/${shopId}/runs/${encodeURIComponent(tail)}`
    );
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Run fetch failed: ${response.status}`));
    }
    return response.json();
  },

  // ── Bulk export ──────────────────────────────────────────────────────
  // Uses the apiFetch-then-presigned-URL pattern (the only correct shape
  // for JWT-gated downloads — see harmony-migration-tracker "Things worth
  // knowing" #6). Backend returns { url } pointing at S3; frontend nav.
  async generateExport(filters = {}) {
    const response = await apiFetch(`${API_BASE_URL}/api/exports`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(filters),
    });
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Export failed: ${response.status}`));
    }
    return response.json(); // { url, key, ... }
  },

  // ── Dashboard ─────────────────────────────────────────────────────────
  async getDashboard() {
    const response = await apiFetch(`${API_BASE_URL}/api/dashboard`);
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Dashboard fetch failed: ${response.status}`));
    }
    return response.json();
  },

  // ── Feedback ─────────────────────────────────────────────────────────
  async submitFeedback({ rating, category, comment, user, page }) {
    const response = await apiFetch(`${API_BASE_URL}/api/feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        rating,
        tool: 'store-cro-audit',
        category: category || '',
        comment: comment || '',
        user: user || 'unknown',
        page: page || '',
      }),
    });
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Feedback submit failed: ${response.status}`));
    }
    return response.json();
  },
};
